class LeagueDropdown extends HTMLElement {
  connectedCallback() {
    const rawId = this.dataset.leagueId;
    this._leagueId = rawId && !rawId.startsWith('gid://')
      ? `gid://shopify/Metaobject/${rawId}`
      : rawId;
    this._shop = this.dataset.shop;
    this._token = this.dataset.token;
    this._collectionsUrl = this.dataset.collectionsUrl || '/collections';
    this._i18nNoTeams = this.dataset.i18nNoTeams || 'No teams found.';
    this._i18nError = this.dataset.i18nError || 'Unable to load teams.';
    this._teams = null;
    this._error = null;

    this._drawerId = this.querySelector('template[data-modal-content]')?.dataset.modalContent;

    this._searchListener = e => {
      if (!e.target.matches('[data-league-dropdown-search]')) return;
      const list = e.target.closest('[data-modal-content]')?.querySelector('[data-league-dropdown-list]');
      this._handleSearch(e.target.value, list);
    };
    document.addEventListener('input', this._searchListener);

    if (this._leagueId && this._shop && this._token) {
      this._fetchTeams();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('input', this._searchListener);
  }

  async _fetchTeams() {
    const query = `query LeagueTeams($cursor: String) {
      metaobjects(type: "sparky--team", first: 250, after: $cursor) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          fields {
            key
            value
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 48 })
                  altText
                }
              }
            }
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  id
                }
              }
            }
          }
        }
      }
    }`;

    try {
      const allNodes = [];
      let cursor = null;
      let page = 0;
      const MAX_PAGES = 20;

      do {
        const res = await fetch(`https://${this._shop}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this._token,
          },
          body: JSON.stringify({ query, variables: { cursor } }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const { data, errors } = await res.json();
        if (errors?.length) throw new Error(errors[0].message);

        const connection = data?.metaobjects;
        allNodes.push(...(connection?.nodes ?? []));
        cursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
        page++;
      } while (cursor && page < MAX_PAGES);

      this._teams = allNodes
        .map(node => this._parseTeam(node))
        .filter(team => team.leagues.some(l => l.id === this._leagueId))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } catch (err) {
      this._error = err.message;
      this._teams = [];
      console.error('[league-dropdown] fetch error:', err);
    }

    // Populate template content for the next open
    const template = this.querySelector('template[data-modal-content]');
    if (template) {
      this._populateList(template.content.querySelector('[data-league-dropdown-list]'));
    }

    // Also update the drawer if it's already open
    if (this._drawerId) {
      const liveList = document.querySelector(
        `[data-modal-content="${this._drawerId}"]:not(template) [data-league-dropdown-list]`
      );
      this._populateList(liveList);
    }
  }

  _parseTeam(node) {
    const team = { id: node.id, leagues: [] };
    for (const field of node.fields) {
      if (field.key === 'logo') {
        team.logo = field.reference;
      } else if (field.key === 'leagues') {
        team.leagues = field.references?.nodes ?? [];
      } else {
        team[field.key] = field.value;
      }
    }
    team._rank = parseInt(team.rank ?? '9999', 10);
    return team;
  }

  _populateList(list) {
    if (!list) return;
    list.innerHTML = '';

    if (this._error || !this._teams?.length) {
      const li = document.createElement('li');
      li.className = 'py-4 text-center text-sm text-dove-grey';
      li.textContent = this._error ? this._i18nError : this._i18nNoTeams;
      list.append(li);
      return;
    }

    for (const team of this._teams) {
      const tag = (team.team_tag || '').toLowerCase();
      const href = team.url || `${this._collectionsUrl}/${tag}`;
      const rawName = team.name || tag;
      const logoUrl = team.logo?.image?.url ?? null;
      const logoAlt = team.logo_alt || rawName;

      const li = document.createElement('li');
      li.dataset.teamName = rawName.replace(/-/g, ' ').toLowerCase();

      const a = document.createElement('a');
      a.href = href;
      a.className = 'flex items-center gap-3.5 py-3.5 pr-3.5 text-sm border-b border-silver text-dove-grey hover:text-primary-dark transition-all ease-in';

      const logoEl = document.createElement(logoUrl ? 'img' : 'div');
      logoEl.className = 'w-4 h-4 object-contain shrink-0 mr-2';
      if (logoUrl) {
        logoEl.src = logoUrl;
        logoEl.alt = logoAlt;
        logoEl.loading = 'lazy';
      }
      a.append(logoEl);

      const nameSpan = document.createElement('span');
      nameSpan.className = 'capitalize';
      nameSpan.innerHTML = this._displayName(rawName, team.override);
      a.append(nameSpan);

      li.append(a);
      list.append(li);
    }
  }

  _displayName(raw, override) {
    const base = (raw || '').replace(/-/g, ' ').toLowerCase();
    if (!override) return base;
    const escaped = override.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return base.replace(escaped, `<span style="text-transform:none">${override}</span>`);
  }

  _handleSearch(query, list) {
    if (!list) return;
    const q = query.toLowerCase().trim();
    list.querySelectorAll('li[data-team-name]').forEach(li => {
      li.hidden = q.length > 0 && !li.dataset.teamName.includes(q);
    });
  }
}

customElements.define('league-dropdown', LeagueDropdown);
